import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle, Clock, AlertTriangle, TrendingUp, Building2, DollarSign } from 'lucide-react';
import salaryDistributionOrchestrationService from '../../../services/salaryDistributionOrchestrationService';

const RealtimeReconciliationDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadDashboard();

    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboard();
      }, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadDashboard = async () => {
    try {
      const data = await salaryDistributionOrchestrationService?.getReconciliationDashboard({});
      
      // Calculate aggregated statistics
      const stats = {
        totalOrchestrations: data?.length || 0,
        activeOrchestrations: data?.filter(o => o?.distributionStatus === 'processing')?.length || 0,
        completedOrchestrations: data?.filter(o => o?.distributionStatus === 'completed')?.length || 0,
        totalEmployees: data?.reduce((sum, o) => sum + (o?.totalEmployees || 0), 0),
        totalAmount: data?.reduce((sum, o) => sum + (o?.totalAmountKina || 0), 0),
        averageReconciliation: data?.length > 0 
          ? data?.reduce((sum, o) => sum + (o?.reconciliationProgressPercentage || 0), 0) / data?.length
          : 0,
        pendingReconciliations: data?.reduce((sum, o) => {
          return sum + (o?.reconciliationSummary?.pending || 0);
        }, 0),
        reconciledPayments: data?.reduce((sum, o) => {
          return sum + (o?.reconciliationSummary?.reconciled || 0);
        }, 0)
      };

      setDashboardData({ orchestrations: data, stats });
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Activity className="w-8 h-8 animate-pulse text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Real-Time Reconciliation Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">
            Last updated: {lastUpdate?.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e?.target?.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            Auto-refresh (30s)
          </label>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg">
            <Activity className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span className="text-sm font-medium text-emerald-700">Live</span>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Active Distributions</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {dashboardData?.stats?.activeOrchestrations}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                of {dashboardData?.stats?.totalOrchestrations} total
              </p>
            </div>
            <Activity className="w-10 h-10 text-blue-600 opacity-50" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Employees</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {dashboardData?.stats?.totalEmployees?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">across all distributions</p>
            </div>
            <Building2 className="w-10 h-10 text-purple-600 opacity-50" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {salaryDistributionOrchestrationService?.formatAmount(dashboardData?.stats?.totalAmount)}
              </p>
              <p className="text-xs text-gray-500 mt-1">distributed</p>
            </div>
            <DollarSign className="w-10 h-10 text-emerald-600 opacity-50" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Avg Reconciliation</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {dashboardData?.stats?.averageReconciliation?.toFixed(0)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {dashboardData?.stats?.reconciledPayments} of {dashboardData?.stats?.reconciledPayments + dashboardData?.stats?.pendingReconciliations} payments
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-yellow-600 opacity-50" />
          </div>
        </div>
      </div>

      {/* Reconciliation Progress */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Reconciliation Progress</h3>
        <div className="space-y-4">
          {dashboardData?.orchestrations?.slice(0, 5)?.map((orch) => (
            <div key={orch?.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{orch?.orchestrationNumber}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {orch?.totalEmployees} employees • {orch?.totalBanks} banks
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${
                    orch?.reconciliationProgressPercentage >= 100 ? 'text-emerald-600' :
                    orch?.reconciliationProgressPercentage >= 50 ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {orch?.reconciliationProgressPercentage?.toFixed(0)}%
                  </span>
                  {orch?.reconciliationProgressPercentage >= 100 ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : orch?.reconciliationProgressPercentage > 0 ? (
                    <Clock className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    orch?.reconciliationProgressPercentage >= 100 ? 'bg-emerald-600' :
                    orch?.reconciliationProgressPercentage >= 50 ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${orch?.reconciliationProgressPercentage || 0}%` }}
                />
              </div>

              {/* Bank Breakdown */}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                {orch?.bankBreakdown?.map((bank, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-600">
                      {bank?.bankName?.toUpperCase()}: {salaryDistributionOrchestrationService?.formatAmount(bank?.totalAmount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reconciliation Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <h4 className="text-sm font-semibold text-emerald-900">Reconciled</h4>
          </div>
          <p className="text-3xl font-bold text-emerald-900">
            {dashboardData?.stats?.reconciledPayments}
          </p>
          <p className="text-xs text-emerald-700 mt-1">payments confirmed</p>
        </div>

        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-6 h-6 text-yellow-600" />
            <h4 className="text-sm font-semibold text-yellow-900">Pending</h4>
          </div>
          <p className="text-3xl font-bold text-yellow-900">
            {dashboardData?.stats?.pendingReconciliations}
          </p>
          <p className="text-xs text-yellow-700 mt-1">awaiting confirmation</p>
        </div>

        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            <h4 className="text-sm font-semibold text-blue-900">Completion Rate</h4>
          </div>
          <p className="text-3xl font-bold text-blue-900">
            {dashboardData?.stats?.completedOrchestrations}
          </p>
          <p className="text-xs text-blue-700 mt-1">
            of {dashboardData?.stats?.totalOrchestrations} distributions
          </p>
        </div>
      </div>
    </div>
  );
};

export default RealtimeReconciliationDashboard;